import { fetchSandboxes as _fetchSandboxes, fetchSuperAdmins } from 'thunk';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { ValueOf } from 'type-fest';
import {
  selectRailsSandboxIsoState,
  selectRailsSandboxesHash,
  selectSandboxReducerRequestStatusesAndErrors,
} from 'SandboxModule/selectors';
import ApiClientManager from 'core/apiClient';
import { FetchSandboxParams } from '../types';
import { useCallback } from 'react';
import useAppNavigation from 'hooks/useAppNavigation';
import { useCurrentContext } from 'hooks/useCurrentContext';
import { RealmTypes } from 'RealmModule/constants/realm';
import { Realm } from 'types';
import { defaultSandboxIsoState } from 'SandboxModule/reducers';
import { checkIsMosaicSandboxTeam } from 'SandboxModule/utils';
import { Tenant } from 'TenantModule/types';
import { SANDBOX_TEAM_CREDENTIALS_WARNING_MESSAGE } from 'SandboxModule/constants';

export enum SandboxIsoStateIds {
  AppbarRealmSelector = 'AppbarRealmSelector',
  SandboxTable = 'SandboxTable',
  SandboxCreationTimer = 'SandboxCreationTimer',
}

export type SandboxIsoStateIdsValues = ValueOf<typeof SandboxIsoStateIds>;

const useSandboxes = () => {
  const dispatch = useAppDispatch();
  const railsSandboxesByIsoState = useAppSelector(selectRailsSandboxIsoState);
  const railsSandboxesHash = useAppSelector(selectRailsSandboxesHash);
  const requestStatuses = useAppSelector(selectSandboxReducerRequestStatusesAndErrors);
  const { navigateToTeamsTable } = useAppNavigation();
  const { realmId: contextRealmId } = useCurrentContext();
  const defaultRealmId = ApiClientManager.getDefaultRealm().realm_id;
  const isUsingNonDefaultRailsSandbox =
    ApiClientManager.getIsRealmEnabled() && contextRealmId !== defaultRealmId;
  const isUsingTry = contextRealmId === 'try';

  const fetchSandboxes = useCallback(
    (isoStateId: ValueOf<typeof SandboxIsoStateIds>, params?: Partial<FetchSandboxParams>) => {
      if (ApiClientManager.getIsRealmEnabled()) {
        let realmTypes = params?.realmTypes || [RealmTypes.sandbox, RealmTypes.subscription]; // Default to fetching all sandboxes types

        // Spec: If Subscription realm sandbox is not enabled, should not fetch for it
        if (!ApiClientManager.getIsSubscriptionRealmEnabled()) {
          realmTypes = realmTypes.filter((realmType) => realmType !== RealmTypes.subscription);
        }

        // Spec: If Regular realm sandbox is not enabled, should not fetch for it
        if (!ApiClientManager.getIsSandboxRealmEnabled()) {
          realmTypes = realmTypes.filter((realmType) => realmType !== RealmTypes.sandbox);
        }

        dispatch(
          _fetchSandboxes({
            includeArchived: true,
            isoStateId,
            ...params,
            realmTypes,
            meta: {
              requestStatusId: isoStateId,
            },
          }),
        );
      }
    },
    [dispatch],
  );

  const getSandboxesByIsoState = useCallback(
    (isoStateId: ValueOf<typeof SandboxIsoStateIds>) => {
      const sandboxIsoStateData = {
        ...(railsSandboxesByIsoState[isoStateId] || defaultSandboxIsoState),
        ...requestStatuses[isoStateId],
      };
      return sandboxIsoStateData;
    },
    [railsSandboxesByIsoState, requestStatuses],
  );

  const getSandbox = useCallback(
    (realmId: string): Realm | undefined => {
      return railsSandboxesHash[realmId];
    },
    [railsSandboxesHash],
  );

  const getRealmIdsByTenant = useCallback(
    (tenantName: string): string[] => {
      return Object.values(railsSandboxesHash)
        .filter((realm) => realm.tenant === tenantName)
        .map((realm) => realm.realm_id);
    },
    [railsSandboxesHash],
  );

  const getSelectedRailsSandbox = useCallback(() => {
    // Use getAuthDefaultRealmId to avoid deprecation warning
    const selectedRailsSandboxRealmId = ApiClientManager.getAuthDefaultRealmId();
    return getSandbox(selectedRailsSandboxRealmId);
  }, [getSandbox]);

  const getDefaultRealm = useCallback(() => {
    return getSandbox(ApiClientManager.getDefaultRealm().realm_id);
  }, [getSandbox]);

  /**
   * @deprecated Auth defaults are now immutable after login.
   * For context switching, navigate with URL params instead:
   * navigate(`/teams-2?tenant=${tenant}&realmId=${realm.realm_id}`)
   *
   * This function is kept for backward compatibility but is a no-op.
   */
  const handleSelectGlobalRailsSandbox = useCallback(
    ({ sandbox: _sandbox, onSelect }: { sandbox: Realm; onSelect?: VoidFunction }) => {
      // No-op: Auth defaults are immutable after login
      // Context switching happens via URL navigation
      onSelect?.();
    },
    [],
  );

  // a warning message to show when the user is on the try tenant try realm Mosaic Sandbox team
  // show on the profile page and set user temporary password modal
  const getSandboxCredentialsWarningMessage = useCallback(
    ({ teamId }: { teamId: number }) => {
      const isMosaicSandboxTeam = checkIsMosaicSandboxTeam(teamId);
      const shouldShowDoNotShareCredientialsWarning = isMosaicSandboxTeam && isUsingTry;

      return shouldShowDoNotShareCredientialsWarning
        ? SANDBOX_TEAM_CREDENTIALS_WARNING_MESSAGE
        : '';
    },
    [isUsingTry],
  );

  return {
    getSandboxesByIsoState,
    getSandbox,
    getRealmIdsByTenant,
    getSelectedRailsSandbox,
    getDefaultRealm,
    handleSelectGlobalRailsSandbox,
    fetchSandboxes,
    isUsingNonDefaultRailsSandbox,
    getSandboxCredentialsWarningMessage,
  };
};

export default useSandboxes;
